import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Calendar, Plus, Save } from "lucide-react";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { api, formatApiErrorDetail } from "../lib/api";

const WEEKDAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const DEFAULT_TEMPLATE = {
  monday: [
    { start: "09:00", end: "12:00", mode_allowed: ["opd", "video"] },
    { start: "17:00", end: "20:00", mode_allowed: ["video"] },
  ],
  tuesday: [{ start: "09:00", end: "12:00", mode_allowed: ["opd", "video"] }],
  wednesday: [{ start: "09:00", end: "12:00", mode_allowed: ["opd", "video"] }],
  thursday: [{ start: "09:00", end: "12:00", mode_allowed: ["opd", "video"] }],
  friday: [
    { start: "09:00", end: "12:00", mode_allowed: ["opd", "video"] },
    { start: "17:00", end: "20:00", mode_allowed: ["video"] },
  ],
  saturday: [{ start: "10:00", end: "13:00", mode_allowed: ["video"] }],
  sunday: [],
};

function todayPlus(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function DoctorAvailability() {
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE);
  const [dateFrom, setDateFrom] = useState(todayPlus(0));
  const [dateTo, setDateTo] = useState(todayPlus(6));
  const [availability, setAvailability] = useState(null);
  const [fees, setFees] = useState({ consultation_fee: 0, video_fee: 0, opd_fee: 0 });
  const [override, setOverride] = useState({ date: todayPlus(0), kind: "holiday", start_time: "", end_time: "", note: "" });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/doctor/me/availability");
      setAvailability(data);
      if (data.fees) setFees(data.fees);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateBlock = (day, idx, field, val) => {
    setTemplate((t) => ({
      ...t,
      [day]: t[day].map((b, i) => (i === idx ? { ...b, [field]: val } : b)),
    }));
  };
  const addBlock = (day) =>
    setTemplate((t) => ({
      ...t,
      [day]: [...t[day], { start: "09:00", end: "12:00", mode_allowed: ["opd", "video"] }],
    }));
  const removeBlock = (day, idx) =>
    setTemplate((t) => ({ ...t, [day]: t[day].filter((_, i) => i !== idx) }));

  const generateSlots = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/doctor/me/slots/bulk", {
        date_from: dateFrom,
        date_to: dateTo,
        weekday_template: Object.fromEntries(
          Object.entries(template).map(([k, blocks]) => [
            k,
            blocks.map((b) => ({
              start: b.start,
              end: b.end,
              mode_allowed: b.mode_allowed,
            })),
          ]),
        ),
      });
      toast.success(`Created ${data.created} slots (${data.skipped} skipped)`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const saveFees = async () => {
    setBusy(true);
    try {
      await api.patch("/doctor/me/fees", {
        consultation_fee: Number(fees.consultation_fee),
        video_fee: Number(fees.video_fee),
        opd_fee: Number(fees.opd_fee),
      });
      toast.success("Fees updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const addOverride = async () => {
    setBusy(true);
    try {
      await api.post("/doctor/me/overrides", override);
      toast.success("Override added");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const blockSlot = async (slotId) => {
    try {
      await api.post(`/doctor/me/slots/${slotId}/block`);
      toast.success("Slot blocked");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="min-h-screen bg-card text-foreground flex flex-col">
      <Navbar />
      <main
        data-testid="doctor-availability-page"
        className="flex-1 px-6 lg:px-10 py-8 lg:py-10 max-w-6xl w-full mx-auto space-y-6"
      >
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Availability & fees</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Set your weekly template, generate slots and update consult fees.
          </p>
        </div>

        {/* Fees */}
        <section className="bg-white border border-border rounded-2xl p-6 shadow-sm">
          <h2 className="font-semibold text-lg">Fees</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
            <div>
              <Label className="text-xs">Consultation ₹</Label>
              <Input
                data-testid="fees-consultation"
                type="number"
                value={fees.consultation_fee}
                onChange={(e) => setFees((f) => ({ ...f, consultation_fee: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Video ₹</Label>
              <Input
                data-testid="fees-video"
                type="number"
                value={fees.video_fee}
                onChange={(e) => setFees((f) => ({ ...f, video_fee: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">OPD ₹</Label>
              <Input
                data-testid="fees-opd"
                type="number"
                value={fees.opd_fee}
                onChange={(e) => setFees((f) => ({ ...f, opd_fee: e.target.value }))}
                className="mt-1"
              />
            </div>
          </div>
          <div className="mt-4">
            <Button
              data-testid="fees-save-btn"
              onClick={saveFees}
              disabled={busy}
              className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground"
            >
              <Save className="h-4 w-4 mr-1.5" /> Save fees
            </Button>
          </div>
        </section>

        {/* Weekly template */}
        <section className="bg-white border border-border rounded-2xl p-6 shadow-sm">
          <h2 className="font-semibold text-lg">Weekly template</h2>
          <p className="text-xs text-muted-foreground mb-4">
            Set time blocks per weekday. Each block becomes one slot per 30 minutes.
          </p>
          <div className="space-y-4">
            {WEEKDAYS.map((day) => (
              <div key={day} data-testid={`weekday-${day}`} className="border border-border rounded-xl p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold capitalize">{day}</div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    data-testid={`weekday-${day}-add-block`}
                    onClick={() => addBlock(day)}
                    className="rounded-full"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add block
                  </Button>
                </div>
                {template[day].length === 0 ? (
                  <div className="text-xs text-muted-foreground">No blocks · off-day</div>
                ) : (
                  <div className="space-y-2">
                    {template[day].map((b, idx) => (
                      <div key={idx} className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-center">
                        <Input
                          type="time"
                          value={b.start}
                          onChange={(e) => updateBlock(day, idx, "start", e.target.value)}
                        />
                        <Input
                          type="time"
                          value={b.end}
                          onChange={(e) => updateBlock(day, idx, "end", e.target.value)}
                        />
                        <Select
                          value={b.mode_allowed.join(",")}
                          onValueChange={(v) =>
                            updateBlock(day, idx, "mode_allowed", v.split(","))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="opd,video">OPD + Video</SelectItem>
                            <SelectItem value="video">Video only</SelectItem>
                            <SelectItem value="opd">OPD only</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => removeBlock(day, idx)}
                          className="rounded-full text-rose-700 border-rose-200 hover:bg-rose-50 hover:text-rose-800"
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-5 pt-4 border-t border-border grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">From</Label>
              <Input
                data-testid="gen-from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input
                data-testid="gen-to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="mt-1"
              />
            </div>
            <div className="flex items-end">
              <Button
                data-testid="gen-slots-btn"
                onClick={generateSlots}
                disabled={busy}
                className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground"
              >
                Generate slots
              </Button>
            </div>
          </div>
        </section>

        {/* Calendar view */}
        <section className="bg-white border border-border rounded-2xl p-6 shadow-sm">
          <h2 className="font-semibold text-lg">Next 14 days</h2>
          {loading || !availability ? (
            <div className="h-40 bg-muted rounded-2xl animate-pulse mt-4" />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mt-4">
              {availability.dates.map((d) => {
                const slots = availability.by_date[d]?.slots || [];
                const booked = slots.filter((s) => s.is_booked).length;
                const ovs = availability.by_date[d]?.overrides || [];
                return (
                  <div
                    key={d}
                    data-testid={`avail-day-${d}`}
                    className="border border-border rounded-xl p-3 text-xs"
                  >
                    <div className="font-semibold">
                      {new Date(d + "T00:00:00").toLocaleDateString("en-IN", {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                      })}
                    </div>
                    <div className="text-muted-foreground mt-1">
                      {slots.length} slots · {booked} booked
                    </div>
                    {ovs.map((o) => (
                      <div key={o.id} className="mt-1 text-amber-700">
                        {o.kind}
                        {o.start_time && ` ${o.start_time}–${o.end_time}`}
                      </div>
                    ))}
                    {slots.filter((s) => !s.is_booked).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {slots
                          .filter((s) => !s.is_booked)
                          .slice(0, 6)
                          .map((s) => (
                            <button
                              key={s.id}
                              type="button"
                              onClick={() => blockSlot(s.id)}
                              data-testid={`avail-slot-${s.id}`}
                              title="Click to block"
                              className="px-1.5 py-0.5 rounded bg-secondary text-secondary-foreground hover:bg-rose-100 hover:text-rose-700"
                            >
                              {s.start_time}
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Override */}
        <section className="bg-white border border-border rounded-2xl p-6 shadow-sm">
          <h2 className="font-semibold text-lg">Add holiday or break</h2>
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 mt-3 items-end">
            <div>
              <Label className="text-xs">Date</Label>
              <Input
                data-testid="override-date"
                type="date"
                value={override.date}
                onChange={(e) => setOverride((o) => ({ ...o, date: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Kind</Label>
              <Select
                value={override.kind}
                onValueChange={(v) => setOverride((o) => ({ ...o, kind: v }))}
              >
                <SelectTrigger data-testid="override-kind" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="holiday">Full day off</SelectItem>
                  <SelectItem value="break">Break</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Start (optional)</Label>
              <Input
                type="time"
                value={override.start_time}
                onChange={(e) => setOverride((o) => ({ ...o, start_time: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">End (optional)</Label>
              <Input
                type="time"
                value={override.end_time}
                onChange={(e) => setOverride((o) => ({ ...o, end_time: e.target.value }))}
                className="mt-1"
              />
            </div>
            <Button
              data-testid="override-save-btn"
              onClick={addOverride}
              disabled={busy}
              className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground"
            >
              Add
            </Button>
          </div>
        </section>
      </main>
    </div>
  );
}
